import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useRouterState } from "@tanstack/react-router"
import { searchFromLocationHref } from "@/features/dashboard/lib/explore-search"
import {
  appliedDrilldownToSearch,
  drilldownSearchKey,
  drilldownSearchSchema,
  drilldownSearchToFilters,
  hasDrilldownSearchParams,
  type DrilldownSearch,
} from "../lib/drilldown-search"
import { useDrilldownFiltersStore } from "../stores/drilldownFilters.store"

export type DrilldownUrlHydrationPhase = "pending" | "hydrated"

export function useDrilldownSearchSync() {
  const navigate = useNavigate()
  const href = useRouterState({ select: (s) => s.location.href })

  const rawSearchRecord = useMemo(() => searchFromLocationHref(href), [href])
  const parsedSearch = useMemo(
    () => drilldownSearchSchema.parse(rawSearchRecord),
    [rawSearchRecord],
  )

  const isPushingRef = useRef(false)
  const lastHydratedKeyRef = useRef<string | null>(null)

  const hydrateFromApplied = useDrilldownFiltersStore((s) => s.hydrateFromApplied)

  const [urlHydrationPhase, setUrlHydrationPhase] =
    useState<DrilldownUrlHydrationPhase>("pending")

  // Bumped on every external URL change (deep link, back/forward, paste) — never on a
  // store-initiated push. Lets consumers tell a user-driven lookback change apart from
  // history navigation that restored custom dates, so the latter is not mistaken for one.
  const [externalHydrationNonce, setExternalHydrationNonce] = useState(0)

  // URL → store hydration (on load, back/forward, deep link)
  useEffect(() => {
    const key = drilldownSearchKey(parsedSearch)

    if (isPushingRef.current) {
      isPushingRef.current = false
      lastHydratedKeyRef.current = key
      return
    }
    if (key === lastHydratedKeyRef.current) return

    lastHydratedKeyRef.current = key

    if (hasDrilldownSearchParams(parsedSearch)) {
      hydrateFromApplied(drilldownSearchToFilters(parsedSearch))
    }

    setUrlHydrationPhase("hydrated")
    setExternalHydrationNonce((nonce) => nonce + 1)
  }, [parsedSearch, hydrateFromApplied])

  const pushAppliedToUrl = useCallback(
    (replace = false) => {
      const state = useDrilldownFiltersStore.getState()
      const drilldownPart = appliedDrilldownToSearch(state)

      // Check if drilldown portion of current URL already matches — skip to avoid history noise
      const currentKey = drilldownSearchKey(parsedSearch)
      const nextKey = drilldownSearchKey({
        ...parsedSearch,
        ...drilldownPart,
      } as DrilldownSearch)
      if (currentKey === nextKey) return

      isPushingRef.current = true
      const next = { ...rawSearchRecord, ...drilldownPart }
      void navigate({ search: next as never, replace })
      lastHydratedKeyRef.current = nextKey
    },
    [navigate, parsedSearch, rawSearchRecord],
  )

  return { pushAppliedToUrl, urlHydrationPhase, parsedSearch, externalHydrationNonce }
}
